import React from "react";
import PropTypes from "prop-types";
import { withStyles } from "@mui/styles";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import LinearProgress from "@mui/material/LinearProgress";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningIcon from "@mui/icons-material/Warning";
import ErrorIcon from "@mui/icons-material/Error";
import StarIcon from "@mui/icons-material/Star";

const styles = (theme) => ({
  card: {
    backgroundColor: "#1C1E20",
    borderRadius: "12px",
    padding: "1.5rem",
    border: "1px solid rgba(232, 163, 61, 0.2)",
    height: "100%",
    display: "flex",
    flexDirection: "column",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: "1rem",
  },
  title: {
    fontSize: "1.1rem",
    fontWeight: 700,
    color: "#ffffff",
  },
  statusIcon: {
    fontSize: "1.5rem",
  },
  scoreRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: "0.5rem",
  },
  scoreLabel: {
    fontSize: "0.9rem",
    color: "rgba(255,255,255,0.7)",
  },
  scoreValue: {
    fontSize: "0.95rem",
    fontWeight: 700,
  },
  progressBar: {
    height: "10px",
    borderRadius: "5px",
    backgroundColor: "rgba(255,255,255,0.1)",
    marginBottom: "0.5rem",
  },
  differenceRow: {
    display: "flex",
    alignItems: "center",
    gap: "0.5rem",
    marginTop: "1rem",
    padding: "0.75rem",
    borderRadius: "8px",
    backgroundColor: "rgba(255,255,255,0.03)",
  },
  differenceText: {
    fontSize: "0.9rem",
    fontWeight: 600,
  },
});

function DimensionScoreBar({ classes, name, yourScore, average, showTip }) {
  const difference = yourScore - average;
  
  // Determine status based on difference
  let status = "average";
  let statusColor = "#64DD17"; // Green
  let statusText = "On Track";
  let StatusIcon = CheckCircleIcon;
  
  if (difference < -15) {
    status = "critical";
    statusColor = "#F44336"; // Red
    statusText = "Needs Attention";
    StatusIcon = ErrorIcon;
  } else if (difference < -5) {
    status = "warning";
    statusColor = "#FFC107"; // Yellow
    statusText = "Room for Improvement";
    StatusIcon = WarningIcon;
  } else if (difference > 5) {
    status = "excellent";
    statusColor = "#00C853"; // Bright Green
    statusText = "Excellent";
    StatusIcon = StarIcon;
  }

  return (
    <Box className={classes.card}>
      <div className={classes.header}>
        <Typography className={classes.title}>{name}</Typography>
        <StatusIcon className={classes.statusIcon} style={{ color: statusColor }} />
      </div>

      <div className={classes.scoreRow}>
        <Typography className={classes.scoreLabel}>Your Score:</Typography>
        <Typography className={classes.scoreValue} style={{ color: "#E8A33D" }}>
          {yourScore} / 100
        </Typography>
      </div>
      <LinearProgress
        variant="determinate"
        value={yourScore}
        className={classes.progressBar}
        sx={{
          "& .MuiLinearProgress-bar": {
            backgroundColor: "#E8A33D",
          },
        }}
      />

      <div className={classes.scoreRow}>
        <Typography className={classes.scoreLabel}>Ouro Average:</Typography>
        <Typography className={classes.scoreValue} style={{ color: "rgba(255,255,255,0.7)" }}>
          {average} / 100
        </Typography>
      </div>
      <LinearProgress
        variant="determinate"
        value={average}
        className={classes.progressBar}
        sx={{
          "& .MuiLinearProgress-bar": {
            backgroundColor: "rgba(255,255,255,0.5)",
          },
        }}
      />

      <div className={classes.differenceRow}>
        <Typography 
          className={classes.differenceText} 
          style={{ color: statusColor }}
        >
          {difference > 0 ? `+${difference}` : difference} ({statusText})
        </Typography>
      </div>

      {showTip && (
        <Box mt={1.5}>
          {showTip}
        </Box>
      )}
    </Box>
  );
}

DimensionScoreBar.propTypes = {
  classes: PropTypes.object.isRequired,
  name: PropTypes.string.isRequired,
  yourScore: PropTypes.number.isRequired,
  average: PropTypes.number.isRequired,
  showTip: PropTypes.node,
};

export default withStyles(styles)(DimensionScoreBar);
